import {
    GetColumnProductsStockInfoQuery,
    MicrostoreColumnGroupWithColumnsFragmentFragment,
    MicrostoreColumnUpdateInput,
    MicrostoreColumnWithShelvesFragmentFragment,
} from 'data/__generated__';

export type MicrostoreLocationExpiryDateFormValues = Omit<
    MicrostoreColumnWithShelvesFragmentFragment['shelves'][number]['locationTable'][number][number]['expiryDates'][number],
    '__typename'
>;

export type MicrostoreLocationFormValues = Omit<
    MicrostoreColumnWithShelvesFragmentFragment['shelves'][number]['locationTable'][number][number],
    '__typename' | 'expiryDates'
> & {
    expiryDates: MicrostoreLocationExpiryDateFormValues[];
};

export type MicrostoreShelfFormValues = Omit<
    MicrostoreColumnWithShelvesFragmentFragment['shelves'][number],
    'locationTable'
> & {
    locationTable: MicrostoreLocationFormValues[][];
};

export type MicrostoreColumnFormValues = Omit<MicrostoreColumnWithShelvesFragmentFragment, 'shelves'> & {
    shelves: MicrostoreShelfFormValues[];
};

export type MicrostoreColumnGroupFormValues = Omit<MicrostoreColumnGroupWithColumnsFragmentFragment, 'columns'> & {
    columns: MicrostoreColumnFormValues[];
};

// Querie values to form values
export function mapMicrostoreColumnGroupToMicrostoreColumnGroupFormValues({
    microstoreColumnGroup,
}: {
    microstoreColumnGroup: MicrostoreColumnGroupWithColumnsFragmentFragment;
}): MicrostoreColumnGroupFormValues {
    return {
        ...microstoreColumnGroup,
    };
}

// Form values to input values
export function mapMicrostoreColumnFormValuesToMicrostoreColumnUpdateInput({
    microstoreColumnFormValues,
}: {
    microstoreColumnFormValues: MicrostoreColumnFormValues;
}): MicrostoreColumnUpdateInput {
    return {
        _id: microstoreColumnFormValues._id,
        name: microstoreColumnFormValues.name,
        isPaused: microstoreColumnFormValues.isPaused,
        shouldUpdateStockOnTransaction: microstoreColumnFormValues.shouldUpdateStockOnTransaction,
        shouldSendNayaxPrices: microstoreColumnFormValues.shouldSendNayaxPrices,
        shouldIgnoreReassortThresholds: microstoreColumnFormValues.shouldIgnoreReassortThresholds,
        positionInColumnGroup: microstoreColumnFormValues.positionInColumnGroup,
        startingDate: microstoreColumnFormValues.startingDate,
        removalDate: microstoreColumnFormValues.removalDate,
        externalColumnId: microstoreColumnFormValues.externalColumnId || null,
        externalColumnType: microstoreColumnFormValues.externalColumnType || null,
        externalColumnConfig: microstoreColumnFormValues.externalColumnConfig
            ? {
                  nayaxMDBIntervals: microstoreColumnFormValues.externalColumnConfig.nayaxMDBIntervals.map(
                      ({ min, max }) => ({ min, max }),
                  ),
              }
            : null,
        avaSalePlaceId: microstoreColumnFormValues.avaSalePlaceId || null,
        useDynamicDelivery: microstoreColumnFormValues.useDynamicDelivery ?? false,
        shelves: microstoreColumnFormValues.shelves.map((shelf) => ({
            _id: shelf._id,
            locationTable: shelf.locationTable.map((locationRow) =>
                locationRow.map(({ _id, expiryDates, productId, replacementProductId, stockToAdd, nayaxMDBCode }) => ({
                    _id,
                    expiryDates: expiryDates.map((expiryDate) => ({ ...expiryDate, __typename: undefined })),
                    productId,
                    replacementProductId,
                    stockToAdd,
                    nayaxMDBCode,
                })),
            ),
        })),
    };
}

type ColumnProductFromQuery = NonNullable<GetColumnProductsStockInfoQuery['column']>['products'][number];

export type MicrostoreProductStockInfoFlat = Omit<ColumnProductFromQuery, '__typename' | 'microstoreStockInfo'> &
    NonNullable<ColumnProductFromQuery['microstoreStockInfo']>;

export type MicrostoreProductsStockInfoFormValues = (MicrostoreProductStockInfoFlat & {
    stockUsedInMicrostore: number;
    isPresentTomorrow: boolean;
})[];

function flattenColumnProducts(products: ColumnProductFromQuery[]): MicrostoreProductStockInfoFlat[] {
    return products
        .filter((product) => product.microstoreStockInfo != null)
        .map(({ microstoreStockInfo, __typename: _, ...product }) => ({
            ...product,
            ...microstoreStockInfo!,
        }));
}

export function mapMicrostoreProductsStockInfoQueryToFormValues({
    microstoreColumnGroupFormValuesArray,
    columnProducts,
}: {
    microstoreColumnGroupFormValuesArray: MicrostoreColumnGroupFormValues[];
    columnProducts: ColumnProductFromQuery[] | undefined;
}): MicrostoreProductsStockInfoFormValues | null {
    if (!columnProducts) return null;

    const flatProducts = flattenColumnProducts(columnProducts);

    const productsQuantitiesInStore = microstoreColumnGroupFormValuesArray.reduce<Record<string, number>>(
        (productsQuantitiesAcc, { columns }) => {
            return columns.reduce<Record<string, number>>(
                (productsQuantitiesColumnGroupAcc, { shelves }) => {
                    return shelves.reduce<Record<string, number>>(
                        (productsQuantitiesColumnAcc, { locationTable }) => {
                            return locationTable.reduce<Record<string, number>>(
                                (productsQuantitiesShelfAcc, locationRow) => {
                                    return locationRow.reduce<Record<string, number>>(
                                        (productsQuantitiesLocationRowAcc, { replacementProductId, stockToAdd }) => {
                                            const newProductsQuantities = { ...productsQuantitiesLocationRowAcc };
                                            if (!replacementProductId) {
                                                return newProductsQuantities;
                                            }
                                            if (!newProductsQuantities[replacementProductId]) {
                                                newProductsQuantities[replacementProductId] = 0;
                                            }
                                            newProductsQuantities[replacementProductId] += stockToAdd;

                                            return newProductsQuantities;
                                        },
                                        { ...productsQuantitiesShelfAcc },
                                    );
                                },
                                { ...productsQuantitiesColumnAcc },
                            );
                        },
                        { ...productsQuantitiesColumnGroupAcc },
                    );
                },
                { ...productsQuantitiesAcc },
            );
        },
        {},
    );
    return flatProducts.map((productStockInfo) => ({
        ...productStockInfo,
        stockUsedInMicrostore: productsQuantitiesInStore[productStockInfo._id] ?? 0,
        isPresentTomorrow: productsQuantitiesInStore[productStockInfo._id] !== undefined,
    }));
}

export function microstoreColumnSomeLocationHelper({
    microstoreColumnFormValues,
    someFunction,
}: {
    microstoreColumnFormValues: MicrostoreColumnFormValues;
    someFunction: (location: MicrostoreLocationFormValues) => boolean;
}): boolean {
    return microstoreColumnFormValues.shelves.some((shelf) =>
        shelf.locationTable.some((locationRow) => locationRow.some((location) => someFunction(location))),
    );
}
